import { useQuery } from '@tanstack/react-query';
import { callFunction } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import type { RelationSex } from '@/lib/patientRelations';
import {
  primaryTelephoneNumber,
  readTelephoneSubscribers,
  type MedcomTelephoneSubscriber,
} from '@/lib/patientStamkortFields';

export interface PatientPickerItem {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  telephone_subscribers: MedcomTelephoneSubscriber[];
}

export const patientPickerQueryKey = (clinicId: string | null, search: string) =>
  ['patient-picker', clinicId, search] as const;

export function usePatientsByIds(clinicId: string | null | undefined, ids: string[]) {
  const uniqueIds = [...new Set(ids.filter(Boolean))];
  return useQuery({
    queryKey: ['patients', 'by-ids', clinicId, uniqueIds] as const,
    enabled: Boolean(clinicId) && uniqueIds.length > 0,
    queryFn: async (): Promise<PatientPickerItem[]> => {
      if (!clinicId || uniqueIds.length === 0) return [];
      const { data, error } = await supabase
        .from('patients')
        .select('id, first_name, last_name, email, phone, telephone_subscribers')
        .eq('clinic_id', clinicId)
        .in('id', uniqueIds);
      if (error) throw error;
      return (data ?? []).map((row) => ({
        id: row.id,
        first_name: row.first_name,
        last_name: row.last_name,
        email: row.email,
        phone: primaryTelephoneNumber(row.telephone_subscribers, row.phone),
        telephone_subscribers: readTelephoneSubscribers(row.telephone_subscribers),
      }));
    },
    staleTime: 60_000,
  });
}

/**
 * Lightweight patient typeahead used by the appointment modal. Reuses the
 * `search_tsv` FTS column from MOD-4. Returns empty list when search is empty
 * (we don't want to fetch all patients on first open).
 */
export function usePatientPicker(
  clinicId: string | null | undefined,
  search: string,
  options?: { statuses?: Array<'active' | 'archived' | 'related'> },
) {
  const trimmed = search.trim();
  const statuses = options?.statuses ?? ['active'];
  return useQuery({
    queryKey: [...patientPickerQueryKey(clinicId ?? null, trimmed), statuses],
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<PatientPickerItem[]> => {
      if (!clinicId) return [];

      let query = supabase
        .from('patients')
        .select('id, first_name, last_name, email, phone, telephone_subscribers')
        .eq('clinic_id', clinicId)
        .in('status', statuses)
        .order('last_name', { ascending: true })
        .limit(20);

      if (trimmed) {
        if (trimmed.length <= 2) {
          const pattern = `%${trimmed}%`;
          query = query.or(
            `first_name.ilike.${pattern},last_name.ilike.${pattern},phone.ilike.${pattern},telephone_search.ilike.${pattern}`,
          );
        } else {
          const tsQuery = trimmed
            .split(/\s+/)
            .filter((t) => t.length > 0)
            .map((t) => `${t}:*`)
            .join(' & ');
          query = query.textSearch('search_tsv', tsQuery, { config: 'simple' });
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []).map((row) => ({
        id: row.id,
        first_name: row.first_name,
        last_name: row.last_name,
        email: row.email,
        phone: primaryTelephoneNumber(row.telephone_subscribers, row.phone),
        telephone_subscribers: readTelephoneSubscribers(row.telephone_subscribers),
      }));
    },
    staleTime: 30_000,
  });
}

type RelationPersonRow = {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  telephone_subscribers: unknown;
};

function toPickerItem(row: RelationPersonRow): PatientPickerItem {
  return {
    id: row.id,
    first_name: row.first_name,
    last_name: row.last_name,
    email: row.email,
    phone: primaryTelephoneNumber(row.telephone_subscribers, row.phone),
    telephone_subscribers: readTelephoneSubscribers(row.telephone_subscribers),
  };
}

/**
 * Person search for a gender-specific relation. Sex is derived from CPR on
 * the server (never returned). A known sex that conflicts is omitted.
 * Unknown sex stays in the list. Empty search does not call the function.
 * Pass a null clinic id to keep the query idle.
 */
export function useRelationPersonSearch(
  clinicId: string | null | undefined,
  search: string,
  sex: RelationSex | null,
  excludePatientId: string,
) {
  const trimmed = search.trim();
  const enabled = Boolean(clinicId) && trimmed.length > 0 && (sex === 'male' || sex === 'female');
  return useQuery({
    queryKey: ['relation-person-search', clinicId ?? null, trimmed, sex, excludePatientId] as const,
    enabled,
    queryFn: async (): Promise<PatientPickerItem[]> => {
      if (!clinicId || (sex !== 'male' && sex !== 'female')) return [];
      const result = await callFunction<
        {
          clinic_id: string;
          search: string;
          sex: RelationSex;
          exclude_patient_id: string;
        },
        { people: RelationPersonRow[] }
      >('search-relation-people', {
        clinic_id: clinicId,
        search: trimmed,
        sex,
        exclude_patient_id: excludePatientId,
      });
      return (result.people ?? [])
        .map(toPickerItem)
        .filter((person) => person.id !== excludePatientId);
    },
    staleTime: 30_000,
  });
}
